import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserPlus,
  Search,
  Loader2,
  Filter,
  Grid3X3,
  List,
  Star,
  MapPin,
  Mail,
  Phone,
  Calendar,
  Award,
  TrendingUp,
  Eye,
  Edit,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import axios from "../axios/AxiosInstance";
import { message } from "antd";
import CreateCandidateModal from "./candidate/CreateCandidateModal";
import { CandidateModal } from "./candidate/CandidateModal";

export default function CandidatsPage() {
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [isAddCandidate, setIsAddCandidate] = useState(false);
  const [isViewCandidate, setIsViewCandidate] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    disponible: 0,
    enMission: 0,
    indisponible: 0
  });

  const calculateStats = (candidatesList) => {
    const total = candidatesList.length;
    const disponible = candidatesList.filter(c => c.status === 'Disponible').length;
    const enMission = candidatesList.filter(c => c.status === 'En mission').length;
    const indisponible = candidatesList.filter(c => c.status === 'Indisponible').length;
    setStats({ total, disponible, enMission, indisponible });
  };

  const search = (word) => {
    setSearchQuery(word);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (word.length === 0) {
      setIsSearchMode(false);
      setStatusFilter('tous');
      loadCandidates(0);
      return;
    }

    setLoadingSearch(true);
    setIsSearchMode(true);

    const timeoutId = setTimeout(() => {
      axios
        .post("tempworker-candidate/freelance/find-by-text", { query: word })
        .then((res) => {
          setCandidates(res.data);
          calculateStats(res.data);
          setTotalPages(1);
          setCurrentPage(0);
          setTotalCandidates(res.data.length);
          setLoadingSearch(false);
        })
        .catch((e) => {
          setLoadingSearch(false);
          console.error(e);
        });
    }, 500);

    setSearchTimeout(timeoutId);
  };

  const filterByStatus = (status) => {
    setStatusFilter(status);
    if (!isSearchMode) {
      loadCandidates(0, status);
    }
  };

  const loadCandidates = useCallback((page = 0, status = null) => {
    setIsLoading(true);
    axios
      .get(`/tempworker-candidate/freelance/paginate/${page}`)
      .then((res) => {
        let data = res.data.data;
        const appliedStatus = status !== undefined && status !== null ? status : statusFilter;
        if (appliedStatus && appliedStatus !== 'tous') {
          data = data.filter(c => c.status === appliedStatus);
        }
        setCandidates(data);
        calculateStats(data);
        setCurrentPage(res.data.page);
        setTotalPages(res.data.totalPages);
        setTotalCandidates(res.data.total);
      })
      .catch((e) => {
        console.log(e);
        message.error("Une erreur s'est produite !");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [statusFilter]);

  useEffect(() => {
    loadCandidates(0);
  }, []);

  const goToPage = (page) => {
    if (page < 0 || page >= totalPages || isSearchMode) return;
    loadCandidates(page);
  };

  const viewCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setIsViewCandidate(true);
  };

  // Fonction pour obtenir l'icône de statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Disponible':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'En mission':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'Indisponible':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  // Fonction pour obtenir la couleur de statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponible':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'En mission':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Indisponible':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="w-full p-8">
        {/* Header Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  Base de Candidats
                </h1>
                <p className="text-lg text-slate-600">
                  Gérez votre vivier de talents et identifiez les meilleurs profils
                </p>
              </div>
            </div>

            <motion.button
              onClick={() => setIsAddCandidate(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <UserPlus className="w-5 h-5" />
              <span>Nouveau candidat</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Loading Overlay */}
        <AnimatePresence>
          {loadingSearch && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-xl p-6 flex items-center space-x-3"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              >
                <Loader2 className="animate-spin h-6 w-6 text-blue-600" />
                <span className="text-slate-700">Recherche en cours...</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modals */}
        {isAddCandidate && (
          <CreateCandidateModal
            open={isAddCandidate}
            candidates={candidates}
            setCandidates={setCandidates}
            onOpenChange={setIsAddCandidate}
          />
        )}
        {isViewCandidate && (
          <CandidateModal
            isOpen={isViewCandidate}
            onClose={() => setIsViewCandidate(false)}
            candidates={candidates}
            setCandidates={setCandidates}
            candidate={selectedCandidate}
          />
        )}

        {/* Statistiques */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total candidats</p>
                <p className="text-3xl font-bold text-slate-900">{isSearchMode ? stats.total : totalCandidates}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Disponibles</p>
                <p className="text-3xl font-bold text-green-600">{stats.disponible}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">En mission</p>
                <p className="text-3xl font-bold text-orange-600">{stats.enMission}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Indisponibles</p>
                <p className="text-3xl font-bold text-red-600">{stats.indisponible}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Barre d'outils moderne */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Recherche */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <motion.input
                type="search"
                onChange={(e) => search(e.target.value)}
                placeholder="Rechercher un candidat (nom, email, compétence...)"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                whileFocus={{ scale: 1.02 }}
              />
            </div>

            {/* Filtres */}
            <div className="flex items-center space-x-3">
              <motion.select
                value={statusFilter}
                onChange={(e) => filterByStatus(e.target.value)}
                className="px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                whileFocus={{ scale: 1.02 }}
              >
                <option value="tous">Tous les statuts</option>
                <option value="Disponible">Disponible</option>
                <option value="En mission">En mission</option>
                <option value="Indisponible">Indisponible</option>
              </motion.select>

              {/* Toggle vue */}
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                <motion.button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors duration-200 ${
                    viewMode === 'grid'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Grid3X3 className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors duration-200 ${
                    viewMode === 'list'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <List className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Affichage des candidats */}
        {isLoading ? (
          <motion.div
            className="flex justify-center items-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600">Chargement des candidats...</p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {candidates.map((candidat, index) => (
                    <motion.div
                      key={candidat._id}
                      className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden cursor-pointer group"
                      onClick={() => viewCandidate(candidat)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.5) }}
                      whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
                    >
                      {/* Header avec avatar */}
                      <div className="p-6 pb-4">
                        <div className="flex items-center space-x-4">
                          <div className="relative">
                            {candidat.image ? (
                              <img
                                src={`data:image/jpeg;base64,${candidat.image}`}
                                alt={`${candidat.firstname} ${candidat.lastname}`}
                                className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
                                {candidat.firstname.charAt(0)}{candidat.lastname.charAt(0)}
                              </div>
                            )}
                            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
                              candidat.status === 'Disponible' ? 'bg-green-500' :
                              candidat.status === 'En mission' ? 'bg-orange-500' : 'bg-red-500'
                            }`}></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-slate-900 truncate capitalize">
                              {candidat.firstname} {candidat.lastname}
                            </h3>
                            <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(candidat.status)}`}>
                              {getStatusIcon(candidat.status)}
                              <span>{candidat.status}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contenu */}
                      <div className="px-6 pb-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{candidat.email}</span>
                          </div>

                          {candidat.phone && (
                            <div className="flex items-center space-x-2 text-sm text-slate-600">
                              <Phone className="w-4 h-4" />
                              <span>{candidat.phone}</span>
                            </div>
                          )}

                          {/* Compétences */}
                          {candidat.qualifications && candidat.qualifications.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-slate-700 mb-2">Compétences clés</p>
                              <div className="flex flex-wrap gap-1">
                                {candidat.qualifications.slice(0, 3).map((comp, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full font-medium"
                                  >
                                    {comp}
                                  </span>
                                ))}
                                {candidat.qualifications.length > 3 && (
                                  <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full font-medium">
                                    +{candidat.qualifications.length - 3}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-xs text-slate-500">Dernière activité</span>
                          </div>
                          {candidat.pld && (
                            <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                              #{candidat.pld}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              // Vue liste
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Candidat
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Compétences
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <AnimatePresence>
                        {candidates.map((candidat, index) => (
                          <motion.tr
                            key={candidat._id}
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => viewCandidate(candidat)}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.5) }}
                            whileHover={{ backgroundColor: '#f8fafc' }}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="relative">
                                  {candidat.image ? (
                                    <img
                                      src={`data:image/jpeg;base64,${candidat.image}`}
                                      alt={`${candidat.firstname} ${candidat.lastname}`}
                                      className="w-10 h-10 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                                      {candidat.firstname.charAt(0)}{candidat.lastname.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-900 capitalize">
                                    {candidat.firstname} {candidat.lastname}
                                  </p>
                                  {candidat.pld && (
                                    <p className="text-xs text-red-600">#{candidat.pld}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <div className="flex items-center space-x-2 text-sm text-slate-600">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate max-w-48">{candidat.email}</span>
                                </div>
                                {candidat.phone && (
                                  <div className="flex items-center space-x-2 text-sm text-slate-600">
                                    <Phone className="w-3 h-3" />
                                    <span>{candidat.phone}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(candidat.status)}`}>
                                {getStatusIcon(candidat.status)}
                                <span>{candidat.status}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1 max-w-48">
                                {candidat.qualifications?.slice(0, 2).map((comp, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full"
                                  >
                                    {comp}
                                  </span>
                                ))}
                                {candidat.qualifications && candidat.qualifications.length > 2 && (
                                  <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full">
                                    +{candidat.qualifications.length - 2}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <motion.button
                                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewCandidate(candidat);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </motion.button>
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {!isSearchMode && totalPages > 1 && candidates.length > 0 && (
              <motion.div
                className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl shadow-sm border border-slate-200 p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-sm text-slate-600">
                  Page <span className="font-semibold text-slate-900">{currentPage + 1}</span> sur{' '}
                  <span className="font-semibold text-slate-900">{totalPages}</span>
                  {' '}&middot;{' '}
                  <span className="font-semibold text-slate-900">{totalCandidates}</span> candidats au total
                </p>

                <div className="flex items-center space-x-1">
                  <motion.button
                    onClick={() => goToPage(0)}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    whileHover={currentPage > 0 ? { scale: 1.05 } : {}}
                    whileTap={currentPage > 0 ? { scale: 0.95 } : {}}
                    title="Première page"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </motion.button>

                  <motion.button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    whileHover={currentPage > 0 ? { scale: 1.05 } : {}}
                    whileTap={currentPage > 0 ? { scale: 0.95 } : {}}
                    title="Page précédente"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </motion.button>

                  {(() => {
                    const pages = [];
                    let start = Math.max(0, currentPage - 2);
                    let end = Math.min(totalPages - 1, currentPage + 2);

                    if (currentPage <= 2) end = Math.min(4, totalPages - 1);
                    if (currentPage >= totalPages - 3) start = Math.max(0, totalPages - 5);

                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <motion.button
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            i === currentPage
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                          whileHover={i !== currentPage ? { scale: 1.05 } : {}}
                          whileTap={{ scale: 0.95 }}
                        >
                          {i + 1}
                        </motion.button>
                      );
                    }
                    return pages;
                  })()}

                  <motion.button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    whileHover={currentPage < totalPages - 1 ? { scale: 1.05 } : {}}
                    whileTap={currentPage < totalPages - 1 ? { scale: 0.95 } : {}}
                    title="Page suivante"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </motion.button>

                  <motion.button
                    onClick={() => goToPage(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    whileHover={currentPage < totalPages - 1 ? { scale: 1.05 } : {}}
                    whileTap={currentPage < totalPages - 1 ? { scale: 0.95 } : {}}
                    title="Dernière page"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Message si aucun candidat */}
            {candidates.length === 0 && !isLoading && (
              <motion.div
                className="text-center py-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="text-slate-400 mb-4">
                  <Users className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun candidat trouvé</h3>
                <p className="text-slate-600 mb-6">
                  {searchQuery ? 'Aucun candidat ne correspond à votre recherche.' : 'Commencez par ajouter votre premier candidat.'}
                </p>
                {!searchQuery && (
                  <motion.button
                    onClick={() => setIsAddCandidate(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Ajouter un candidat
                  </motion.button>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
